/**
 * Per-school receipt / invoice display template defaults and resolver.
 * Missing keys always fall back to current product behaviour.
 */

export const BASE_REDUCTION_DISPLAY = {
  HIDDEN: "hidden",
  TOTAL: "total",
  PERCENT_PER_LINE: "percentPerLine",
};

export const TITLE_MODE = {
  DEFAULT: "default",
  CUSTOM: "custom",
};

export const TABLE_NOTE_ALIGNS = ["auto", "left", "center", "right"];

/** Defaults = current printInvoice behaviour when nothing is configured */
export const DOCUMENT_TEMPLATE_DEFAULTS = Object.freeze({
  showLogo: true,
  titleMode: TITLE_MODE.DEFAULT,
  customTitle: "",
  includeSchoolTypeInTitle: true,
  showFooterText: false,
  footerText: "",
  /** Free text printed in the white space right below the payment/summary tables */
  showTableNote: false,
  tableNoteText: "",
  tableNoteColor: "#000000",
  /** pt, clamped 6–24 */
  tableNoteFontSize: 10,
  /** auto = per line by script (Arabic right, others left) */
  tableNoteAlign: "auto",
  showClass: true,
  showTuitionSummary: false,
  showActivitySummary: false,
  showPaymentMethodDetails: false,
  showOnTransReduction: true,
  baseReductionDisplay: BASE_REDUCTION_DISPLAY.HIDDEN,
  showCachetSignatureLabel: true,
  showStamp: false,
  /** Multiplier for stamp size on PDF (1 = 55pt). Clamped 0.5–2.5 */
  stampScale: 1,
  showSignature: false,
  autoPrint: false,
  copiesPerPage: 1,
});

/**
 * Invoice-only fiscal defaults (Phase B).
 * Rate/amount null = fall back to VUE_APP_TVA / VUE_APP_FISCAL (current product).
 * Retenue off by default (not used on parent invoices historically).
 */
export const INVOICE_FISCAL_DEFAULTS = Object.freeze({
  tvaApplicable: true,
  showTva: true,
  /** Fraction e.g. 0.07; null = env */
  tvaRate: null,
  timbreApplicable: true,
  showTimbre: true,
  /** Absolute amount e.g. 1; null = env */
  timbreAmount: null,
  retenueApplicable: false,
  showRetenue: false,
  /** Percent of TTC, same pattern as fournisseur retenue */
  retenuePercent: 0,
});

/**
 * Phase C — document number format (per receipt / invoice).
 * custom:false = current product YYYYMM + 3-digit seq (e.g. 202609001).
 */
export const NUMBER_FORMAT_DEFAULTS = Object.freeze({
  custom: false,
  prefix: "",
  datePattern: "YYYYMM",
  sequencePadding: 4,
  separator: "-",
  pattern: "",
  /** First sequence number (continue numbering from another system) */
  startNumber: 1,
});

export const MAX_START_NUMBER = 99999999;

export function clampStartNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 1) return NUMBER_FORMAT_DEFAULTS.startNumber;
  return Math.min(MAX_START_NUMBER, Math.floor(n));
}

export const NUMBER_DATE_PATTERNS = [
  "none",
  "YYYYMM",
  "YYYY",
  "YY",
  "YYYY-MM",
  "DD.MM.YYYY",
];

/**
 * @param {object|null|undefined} stored
 */
export function resolveNumberFormat(stored) {
  const s = stored && typeof stored === "object" ? stored : {};
  const datePattern = NUMBER_DATE_PATTERNS.includes(s.datePattern)
    ? s.datePattern
    : NUMBER_FORMAT_DEFAULTS.datePattern;
  const padding = Number(s.sequencePadding);
  return {
    custom: coalesceBool(s.custom, NUMBER_FORMAT_DEFAULTS.custom),
    prefix:
      typeof s.prefix === "string"
        ? s.prefix.trim().slice(0, 32)
        : NUMBER_FORMAT_DEFAULTS.prefix,
    datePattern,
    sequencePadding:
      Number.isFinite(padding) && padding >= 3 && padding <= 8
        ? Math.round(padding)
        : NUMBER_FORMAT_DEFAULTS.sequencePadding,
    separator:
      typeof s.separator === "string"
        ? s.separator.slice(0, 3)
        : NUMBER_FORMAT_DEFAULTS.separator,
    pattern:
      typeof s.pattern === "string"
        ? normalizePatternClient(s.pattern)
        : NUMBER_FORMAT_DEFAULTS.pattern,
    startNumber: clampStartNumber(s.startNumber),
  };
}

function normalizePatternClient(pattern) {
  const p = typeof pattern === "string" ? pattern.trim().slice(0, 80) : "";
  if (!p) return "";
  if (/\{SEQ\}/i.test(p)) return p;
  return `${p}-{SEQ}`;
}

function sanitizeSerialForFilesClient(serial) {
  return String(serial || "").replace(/[/\\:*?"<>|]/g, "-");
}

function padSeq(seq, padding) {
  return String(Math.max(1, Number(seq) || 1)).padStart(padding, "0");
}

function formatDatePartClient(date, datePattern) {
  const d = date instanceof Date ? date : new Date(date);
  const yyyy = String(d.getFullYear());
  const yy = yyyy.slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  if (!datePattern || datePattern === "none") return "";
  if (datePattern === "YYYYMM") return yyyy + mm;
  if (datePattern === "YYYY") return yyyy;
  if (datePattern === "YY") return yy;
  if (datePattern === "YYYY-MM") return `${yyyy}-${mm}`;
  if (datePattern === "DD.MM.YYYY") return `${dd}.${mm}.${yyyy}`;
  return yyyy + mm;
}

/**
 * Example serial for settings preview (does not consume counter).
 * @param {object|null|undefined} numberFormat
 * @param {Date} [date]
 * @param {number} [seq] - defaults to the configured start number
 */
export function previewDocumentSerial(numberFormat, date = new Date(), seq = null) {
  const cfg = resolveNumberFormat(numberFormat);
  if (seq == null) seq = cfg.startNumber;
  const d = date instanceof Date ? date : new Date(date);
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = yyyy.slice(-2);

  if (!cfg.custom) {
    return yyyy + mm + padSeq(seq, 3);
  }

  const seqStr = padSeq(seq, cfg.sequencePadding);
  if (cfg.pattern) {
    return sanitizeSerialForFilesClient(
      cfg.pattern
        .replace(/\{PREFIX\}/gi, cfg.prefix || "")
        .replace(/\{YYYYMM\}/g, yyyy + mm)
        .replace(/\{YYYY\}/g, yyyy)
        .replace(/\{YY\}/g, yy)
        .replace(/\{MM\}/g, mm)
        .replace(/\{DD\}/g, dd)
        .replace(/\{SEQ\}/gi, seqStr)
    );
  }

  const parts = [];
  if (cfg.prefix) parts.push(cfg.prefix);
  const datePart = formatDatePartClient(d, cfg.datePattern);
  if (datePart) parts.push(datePart);
  parts.push(seqStr);
  return sanitizeSerialForFilesClient(parts.join(cfg.separator));
}

/**
 * @param {object|null|undefined} building
 * @param {'receipt'|'invoice'} docType
 * @returns {object}
 */
export function resolveDocumentTemplate(building, docType) {
  const key = docType === "invoice" ? "invoice" : "receipt";
  const stored =
    (building &&
      building.config &&
      building.config.documentTemplates &&
      building.config.documentTemplates[key]) ||
    {};

  const copiesPerPage =
    Number(stored.copiesPerPage) === 2 ? 2 : DOCUMENT_TEMPLATE_DEFAULTS.copiesPerPage;

  const baseReductionDisplay = Object.values(BASE_REDUCTION_DISPLAY).includes(
    stored.baseReductionDisplay
  )
    ? stored.baseReductionDisplay
    : DOCUMENT_TEMPLATE_DEFAULTS.baseReductionDisplay;

  const titleMode =
    stored.titleMode === TITLE_MODE.CUSTOM
      ? TITLE_MODE.CUSTOM
      : TITLE_MODE.DEFAULT;

  const stampScale = clampStampScale(
    stored.stampScale ?? DOCUMENT_TEMPLATE_DEFAULTS.stampScale
  );

  const resolved = {
    ...DOCUMENT_TEMPLATE_DEFAULTS,
    ...stored,
    titleMode,
    baseReductionDisplay,
    copiesPerPage,
    stampScale,
    showLogo: coalesceBool(stored.showLogo, DOCUMENT_TEMPLATE_DEFAULTS.showLogo),
    includeSchoolTypeInTitle: coalesceBool(
      stored.includeSchoolTypeInTitle,
      DOCUMENT_TEMPLATE_DEFAULTS.includeSchoolTypeInTitle
    ),
    showFooterText: coalesceBool(
      stored.showFooterText,
      DOCUMENT_TEMPLATE_DEFAULTS.showFooterText
    ),
    showClass: coalesceBool(stored.showClass, DOCUMENT_TEMPLATE_DEFAULTS.showClass),
    // Legacy single toggle `showPaymentSummary` seeds both until saved again
    showTuitionSummary: coalesceBool(
      stored.showTuitionSummary,
      coalesceBool(stored.showPaymentSummary, DOCUMENT_TEMPLATE_DEFAULTS.showTuitionSummary)
    ),
    showActivitySummary: coalesceBool(
      stored.showActivitySummary,
      coalesceBool(stored.showPaymentSummary, DOCUMENT_TEMPLATE_DEFAULTS.showActivitySummary)
    ),
    showPaymentMethodDetails: coalesceBool(
      stored.showPaymentMethodDetails,
      DOCUMENT_TEMPLATE_DEFAULTS.showPaymentMethodDetails
    ),
    showOnTransReduction: coalesceBool(
      stored.showOnTransReduction,
      DOCUMENT_TEMPLATE_DEFAULTS.showOnTransReduction
    ),
    showCachetSignatureLabel: coalesceBool(
      stored.showCachetSignatureLabel,
      DOCUMENT_TEMPLATE_DEFAULTS.showCachetSignatureLabel
    ),
    showStamp: coalesceBool(stored.showStamp, DOCUMENT_TEMPLATE_DEFAULTS.showStamp),
    showSignature: coalesceBool(
      stored.showSignature,
      DOCUMENT_TEMPLATE_DEFAULTS.showSignature
    ),
    autoPrint: coalesceBool(stored.autoPrint, DOCUMENT_TEMPLATE_DEFAULTS.autoPrint),
    customTitle:
      typeof stored.customTitle === "string"
        ? stored.customTitle
        : DOCUMENT_TEMPLATE_DEFAULTS.customTitle,
    footerText:
      typeof stored.footerText === "string"
        ? stored.footerText
        : DOCUMENT_TEMPLATE_DEFAULTS.footerText,
    showTableNote: coalesceBool(
      stored.showTableNote,
      DOCUMENT_TEMPLATE_DEFAULTS.showTableNote
    ),
    tableNoteText:
      typeof stored.tableNoteText === "string"
        ? stored.tableNoteText
        : DOCUMENT_TEMPLATE_DEFAULTS.tableNoteText,
    tableNoteColor: normalizeHexColor(stored.tableNoteColor),
    tableNoteFontSize: clampTableNoteFontSize(stored.tableNoteFontSize),
    tableNoteAlign: TABLE_NOTE_ALIGNS.includes(stored.tableNoteAlign)
      ? stored.tableNoteAlign
      : DOCUMENT_TEMPLATE_DEFAULTS.tableNoteAlign,
  };

  if (key === "invoice") {
    resolved.fiscal = resolveInvoiceFiscalConfig(stored.fiscal);
  }
  resolved.numberFormat = resolveNumberFormat(stored.numberFormat);

  return resolved;
}

/**
 * Stored fiscal shape for settings form (null rates = use env).
 * @param {object|null|undefined} stored
 */
export function resolveInvoiceFiscalConfig(stored) {
  const s = stored && typeof stored === "object" ? stored : {};
  return {
    tvaApplicable: coalesceBool(s.tvaApplicable, INVOICE_FISCAL_DEFAULTS.tvaApplicable),
    showTva: coalesceBool(s.showTva, INVOICE_FISCAL_DEFAULTS.showTva),
    tvaRate: nullableNumber(s.tvaRate),
    timbreApplicable: coalesceBool(
      s.timbreApplicable,
      INVOICE_FISCAL_DEFAULTS.timbreApplicable
    ),
    showTimbre: coalesceBool(s.showTimbre, INVOICE_FISCAL_DEFAULTS.showTimbre),
    timbreAmount: nullableNumber(s.timbreAmount),
    retenueApplicable: coalesceBool(
      s.retenueApplicable,
      INVOICE_FISCAL_DEFAULTS.retenueApplicable
    ),
    showRetenue: coalesceBool(s.showRetenue, INVOICE_FISCAL_DEFAULTS.showRetenue),
    retenuePercent: clampPercent(
      s.retenuePercent ?? INVOICE_FISCAL_DEFAULTS.retenuePercent
    ),
  };
}

/**
 * Effective rates for create/calc/PDF preview (env fallback when null).
 * @param {object|null|undefined} fiscalConfig - from resolveInvoiceFiscalConfig
 * @param {{ tva?: number, fiscal?: number }} [envOverrides]
 */
export function getEffectiveFiscalRates(fiscalConfig, envOverrides = {}) {
  const f = resolveInvoiceFiscalConfig(fiscalConfig);
  const envTva = Number(
    envOverrides.tva != null ? envOverrides.tva : process.env.VUE_APP_TVA
  );
  const envFiscal = Number(
    envOverrides.fiscal != null ? envOverrides.fiscal : process.env.VUE_APP_FISCAL
  );
  const fallbackTva = Number.isFinite(envTva) ? envTva : 0.07;
  const fallbackFiscal = Number.isFinite(envFiscal) ? envFiscal : 1;

  const tvaRate = f.tvaApplicable
    ? f.tvaRate != null && Number.isFinite(f.tvaRate)
      ? f.tvaRate
      : fallbackTva
    : 0;

  const timbreAmount = f.timbreApplicable
    ? f.timbreAmount != null && Number.isFinite(f.timbreAmount)
      ? f.timbreAmount
      : fallbackFiscal
    : 0;

  const retenuePercent = f.retenueApplicable
    ? clampPercent(f.retenuePercent)
    : 0;

  return {
    ...f,
    tvaRate,
    timbreAmount,
    retenuePercent,
  };
}

/**
 * Resolve effective fiscal for a building (facture only).
 * @param {object|null|undefined} building
 */
export function resolveBuildingInvoiceFiscal(building) {
  const template = resolveDocumentTemplate(building, "invoice");
  return getEffectiveFiscalRates(template.fiscal);
}

/**
 * Compute HT / TVA / timbre / retenue from TTC excl. timbre base (fees after reduction).
 * Matches parentInvoiceController: horsTimbre = base; HT = horsTimbre*(1-tva); etc.
 * @param {number} amountAfterReduction - fees total after on-trans reduction (no timbre)
 * @param {object} rates - from getEffectiveFiscalRates
 * @param {boolean} isFacture
 */
export function computeFactureFiscalAmounts(
  amountAfterReduction,
  rates,
  isFacture = true
) {
  const base = Number(amountAfterReduction) || 0;
  if (!isFacture) {
    return {
      fiscal: 0,
      tva: 0,
      montantHT: base,
      montantTVA: 0,
      montantTotal: base,
      retenuePercent: 0,
      montantRetenue: 0,
      netApresRetenue: base,
    };
  }

  const tvaRate = Number(rates.tvaRate) || 0;
  const fiscal = Number(rates.timbreAmount) || 0;
  const montantHorsTimbre = base;
  const montantHT = Number((montantHorsTimbre - montantHorsTimbre * tvaRate).toFixed(3));
  const montantTVA = Number((montantHorsTimbre * tvaRate).toFixed(3));
  const montantTotal = Number((montantHorsTimbre + fiscal).toFixed(3));
  const retenuePercent = Number(rates.retenuePercent) || 0;
  const montantRetenue = Number(
    ((montantTotal * retenuePercent) / 100).toFixed(3)
  );
  const netApresRetenue = Number((montantTotal - montantRetenue).toFixed(3));

  return {
    fiscal,
    tva: tvaRate,
    montantHT,
    montantTVA,
    montantTotal,
    retenuePercent,
    montantRetenue,
    netApresRetenue,
  };
}

function coalesceBool(value, fallback) {
  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1 || value === "1") return true;
  if (value === "false" || value === 0 || value === "0") return false;
  return fallback;
}

function nullableNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function clampPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(100, n);
}

export function clampTableNoteFontSize(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DOCUMENT_TEMPLATE_DEFAULTS.tableNoteFontSize;
  return Math.min(24, Math.max(6, Math.round(n)));
}

export function normalizeHexColor(value) {
  if (typeof value !== "string") return DOCUMENT_TEMPLATE_DEFAULTS.tableNoteColor;
  const v = value.trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(v)) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
  }
  return DOCUMENT_TEMPLATE_DEFAULTS.tableNoteColor;
}

export function clampStampScale(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DOCUMENT_TEMPLATE_DEFAULTS.stampScale;
  return Math.min(2.5, Math.max(0.5, Math.round(n * 10) / 10));
}

/**
 * Map ParentInvoice typeFR to template key.
 * @param {string} typeFR
 * @returns {'receipt'|'invoice'}
 */
export function docTypeFromTypeFR(typeFR) {
  return typeFR === "F" ? "invoice" : "receipt";
}

/**
 * Sample invoice-shaped record for settings preview.
 * @param {string} typeFR
 * @param {object|null} [fiscalConfig] - form fiscal when previewing facture
 */
export function buildPreviewRecord(typeFR = "R", fiscalConfig = null, numberFormat = null) {
  const baseAfterReduction = 250;
  const rates =
    typeFR === "F"
      ? getEffectiveFiscalRates(fiscalConfig)
      : getEffectiveFiscalRates({
          tvaApplicable: false,
          timbreApplicable: false,
          retenueApplicable: false,
        });
  const amounts = computeFactureFiscalAmounts(
    baseAfterReduction,
    rates,
    typeFR === "F"
  );

  return {
    typeFR,
    serial: previewDocumentSerial(numberFormat, new Date()),
    date: new Date(),
    addressedTo: {
      name: "Parent Exemple",
      type: "PARTICULAR",
    },
    paymentMethod: ["ESPESE", "CHEQUE"],
    montantEspece: 150,
    montantCheque: 100,
    montantTPE: 0,
    montantTraite: 0,
    montantVirement: 0,
    cheques: [
      {
        numero: "123456",
        titulaire: "Parent Exemple",
        montant: 100,
      },
    ],
    tpe: { montant: 0, transactionId: "" },
    onTransReduction: 5,
    montantHtBrut: 250,
    montantHT: amounts.montantHT,
    montantTVA: amounts.montantTVA,
    fiscal: amounts.fiscal,
    tva: amounts.tva,
    montantTotal: amounts.montantTotal,
    retenuePercent: amounts.retenuePercent,
    montantRetenue: amounts.montantRetenue,
    netApresRetenue: amounts.netApresRetenue,
    details: [
      {
        studentName: "Élève Exemple",
        typeReglement: "Scolarité/CE1",
        typeAS: "S",
        daily: false,
        tva: amounts.tva,
        montantHT: amounts.montantHT,
        montantTotal: baseAfterReduction,
        fees: {
          "2025-09": {
            paid: 150,
            remaining: 50,
            reduction: 10,
          },
          "2025-10": {
            paid: 100,
            remaining: 100,
            reduction: 0,
          },
        },
        payment: {
          mode: "M",
          months: {
            "2025-09": { paid: 150, remaining: 50, amount: 200 },
            "2025-10": { paid: 100, remaining: 100, amount: 200 },
            "2025-11": { paid: 0, remaining: 200, amount: 200 },
          },
          tranches: {},
          repReduction: { "2025-09": 10, "2025-10": 0 },
          classroom: { name: "CE1", enrollmentConfirmed: false },
        },
      },
    ],
    paymentSummary: buildPreviewPaymentSummary(),
  };
}

/** Sample summary for the settings preview (10 months × 200, 4 paid; 1 activity). */
function buildPreviewPaymentSummary() {
  const months = [
    "2025-09-01", "2025-10-01", "2025-11-01", "2025-12-01", "2026-01-01",
    "2026-02-01", "2026-03-01", "2026-04-01", "2026-05-01", "2026-06-01",
  ];
  const line = (feeKey, amount, extra = {}) => ({
    feeKey, amount, mode: "M", daily: false, name: "", student: "", partial: false, ...extra,
  });
  return {
    tuition: {
      totalToPay: 2000,
      totalPaid: 800,
      totalUnpaid: 1200,
      paid: months.slice(0, 4).map((m) => line(m, 200)),
      unpaid: months.slice(4).map((m) => line(m, 200)),
    },
    activity: {
      totalToPay: 600,
      totalPaid: 300,
      totalUnpaid: 300,
      paid: [line("1", 300, { mode: "T", name: "Piscine", typeAS: "A" })],
      unpaid: [line("2", 300, { mode: "T", name: "Piscine", typeAS: "A" })],
    },
  };
}
